// src/components/Control/Operator/Camera.tsx
// Камеры: окно видео с наложениями (люди, распознанное), панель камеры для
// desktop, плавающее превью для mobile и выбор камеры. Видео вписано в блок
// 4:3, наложения совпадают с кадром при любом размере.
import React, { useRef, useState } from 'react';
import { useRobot, useUi, useUiActions, useTelemetryState, useHasCam2, useHasChassis, CamId, CamChoice } from '../robot/RobotContext';
import { STALE } from '../robot/telemetry';
import { useCameraFeed } from '../robot/cameraFeed';
import { putJson } from '../../../hooks/useZenohSubscription';
import PersonOverlay from '../ControlPanel/PersonOverlay';
import VisionOverlay from '../Features/VisionOverlay';
import Cam2Obstacle from '../ControlPanel/Cam2Obstacle';
import { PanTiltPad } from './PanTilt';
import { CameraTrack } from './CameraTrack';
import { LaserDot } from './Laser';
import { Shutter } from './Snapshots';
import { TrainerBadge } from './Trainer';

import { Icon, Segmented, usePopover } from './ui';
import { useTick } from '../../../hooks/useTick';

export const CAM_LABELS: Record<CamId, string> = { '0': 'Камера 1', '1': 'Камера 2' };
const CAM_TITLES: Record<CamId, string> = { '0': 'Камера 1 (Pi)', '1': 'Камера 2 (ESP32-S3)' };

export const CameraView: React.FC<{
  cam: CamId;
  overlays?: boolean;
  badge?: boolean;
  shutter?: boolean;
  onClick?: () => void;
  className?: string;
}> = ({ cam, overlays = true, badge = true, shutter = overlays, onClick, className }) => {
  useTick(1000);
  const { session, robotId } = useRobot();
  const show = useUi(u => u.show[cam]);
  const key = show ? `robot/${robotId}/cams/${cam}` : null;
  const feed = useCameraFeed(session, key);
  const stale = !feed.at || Date.now() - feed.at > 3000;
  // С какого момента ждём кадры этой камеры
  const since = useRef({ key, at: Date.now() });
  if (since.current.key !== key) since.current = { key, at: Date.now() };
  const waitedLong = Date.now() - since.current.at > 5000;
  const asleep = useTelemetryState(t => !!t.status?.power?.asleep && Date.now() - t.statusAt < STALE.status);

  let message: string | null = null;
  if (!session) message = 'Нет связи с сервером';
  else if (!show) message = 'Показ камеры выключен в этом браузере';
  else if (asleep) message = 'Робот спит — камера выключена';
  else if (feed.error) message = `Ошибка: ${feed.error}`;
  else if (!feed.url) message = waitedLong ? 'Камера недоступна — кадров нет (передача выключена?)' : 'Жду кадры…';
  else if (stale) message = 'Камера недоступна — кадров нет';

  return (
    <div className={`op-cam ${className ?? ''} ${onClick ? 'clickable' : ''}`} onClick={onClick}
         role={onClick ? 'button' : undefined} tabIndex={onClick ? 0 : undefined}
         onKeyDown={onClick ? e => (e.key === 'Enter' ? onClick() : undefined) : undefined}
         aria-label={onClick ? `${CAM_LABELS[cam]}: открыть` : undefined}>
      <div className="op-cam-frame">
        {feed.url && <img src={feed.url} alt={`Видео: ${CAM_TITLES[cam]}`} className={stale ? 'stale' : ''} />}
        {overlays && cam === '0' && show && (
          <>
            <PersonOverlay zenohSession={session} robotId={robotId} />
            <VisionOverlay zenohSession={session} robotId={robotId} />
            <PanTiltPad />
            <CameraTrack />
            <LaserDot />
            <TrainerBadge />
          </>
        )}
        {message && <div className="op-cam-msg">{message}</div>}
        {shutter && show && <Shutter cam={cam} />}
        {badge && (
          <span className={`op-cam-badge ${stale ? 'bad' : ''}`}>
            {CAM_LABELS[cam]}{feed.url && !stale ? ` · ${feed.fps.toFixed(0)} к/с` : ''}
          </span>
        )}
      </div>
    </div>
  );
};

// Кадры выбранной камеры (или обе)
export const CameraStage: React.FC<{ choice: CamChoice; onOpen?: (cam: CamId) => void }> = ({ choice, onOpen }) => (
  <div className={`op-cam-stage ${choice === 'split' ? 'split' : ''}`}>
    {choice === 'split' ? (
      <>
        <CameraView cam="0" onClick={onOpen && (() => onOpen('0'))} />
        <CameraView cam="1" onClick={onOpen && (() => onOpen('1'))} />
      </>
    ) : (
      <CameraView cam={choice} onClick={onOpen && (() => onOpen(choice))} />
    )}
  </div>
);

// Выбранная камера; без камеры 2 (Pi без шасси) — всегда камера 1
export function useCamChoice(): CamChoice {
  const cam = useUi(u => u.cam);
  return useHasCam2() ? cam : '0';
}

export const CamSelector: React.FC = () => {
  const cam = useUi(u => u.cam);
  const { setCam } = useUiActions();
  if (!useHasCam2()) return null;
  return (
    <Segmented<CamChoice> label="Камера" value={cam} onChange={setCam}
               options={[['0', 'Камера 1'], ['1', 'Камера 2'], ['split', 'Обе']]} />
  );
};

// Действия с кадром: «Что в кадре?», записи, снимки, «Запомнить человека»
export const CameraActions: React.FC<{ menu?: boolean }> = ({ menu }) => {
  const { session, robotId, notify } = useRobot();
  const { openSettings } = useUiActions();
  const pop = usePopover();
  const [name, setName] = useState('');
  const describe = () => putJson(session, `robot/${robotId}/vision/cmd`, { cmd: 'describe', speak: false })
    .then(ok => notify(ok ? 'Спрашиваю робота, что в кадре…' : 'Не удалось отправить команду', ok ? 'info' : 'error'));
  const remember = () => {
    putJson(session, `robot/${robotId}/people/cmd`, { cmd: 'remember', ...(name.trim() ? { name: name.trim() } : {}) })
      .then(ok => notify(ok ? 'Запоминаю ближайшего человека в кадре камеры 1' : 'Не удалось отправить команду', ok ? 'success' : 'error'));
    setName('');
    pop.setOpen(false);
  };
  return (
    <div className="op-pop-wrap" ref={pop.ref}>
      {menu ? (
        <button type="button" className="op-icon-btn" aria-label="Действия с камерой" aria-expanded={pop.open} onClick={() => pop.setOpen(!pop.open)}>
          <Icon name="more" />
        </button>
      ) : (
        <div className="op-row">
          <button type="button" className="op-btn" onClick={describe} disabled={!session}>Что в кадре?</button>
          <button type="button" className="op-btn" onClick={() => openSettings('recording')}>Записи</button>
          <button type="button" className="op-btn" onClick={() => openSettings('snapshots')}>Снимки</button>
          <button type="button" className="op-btn" onClick={() => openSettings('trainer')}>Тренер</button>
          <button type="button" className="op-btn" onClick={() => pop.setOpen(!pop.open)} disabled={!session} aria-expanded={pop.open}>
            Запомнить человека…
          </button>
        </div>
      )}
      {pop.open && (
        <div className={`op-popover ${menu ? 'right' : 'up'}`} role="dialog" aria-label="Действия с камерой">
          {menu && (
            <>
              <button type="button" className="op-menu-item" onClick={() => { describe(); pop.setOpen(false); }}>Что в кадре?</button>
              <button type="button" className="op-menu-item" onClick={() => { pop.setOpen(false); openSettings('cameras'); }}>Настройки камер (FPS, передача)…</button>
              <button type="button" className="op-menu-item" onClick={() => { pop.setOpen(false); openSettings('recording'); }}>Записи с камеры…</button>
              <button type="button" className="op-menu-item" onClick={() => { pop.setOpen(false); openSettings('snapshots'); }}>Снимки: куда, набор, серия, выгрузка…</button>
              <button type="button" className="op-menu-item" onClick={() => { pop.setOpen(false); openSettings('trainer'); }}>Фитнес-тренер…</button>
              <div className="op-pop-title">Запомнить человека</div>
            </>
          )}
          <p className="op-muted">Ближайший человек в кадре камеры 1 (должен быть виден хотя бы полсекунды).</p>
          <div className="op-row">
            <input type="text" value={name} maxLength={40} placeholder="имя (необязательно)" aria-label="Имя человека"
                   onChange={e => setName(e.target.value)} onKeyDown={e => e.key === 'Enter' && remember()} />
            <button type="button" className="op-btn primary" onClick={remember}>Запомнить</button>
          </div>
        </div>
      )}
    </div>
  );
};

// Desktop: панель камеры справа от карты
export const CameraPane: React.FC = () => {
  const cam = useCamChoice();
  const layout = useUi(u => u.layout);
  const chassis = useHasChassis();
  const { setLayout } = useUiActions();
  return (
    <section className="op-cam-pane" aria-label="Камеры">
      <CameraStage choice={cam} onOpen={layout === 'camera' || !chassis ? undefined : () => setLayout('camera')} />
      <div className="op-cam-tools">
        <CamSelector />
        {(cam === '1' || cam === 'split') && <Cam2Obstacle compact={cam === 'split'} />}
        <CameraActions />
      </div>
    </section>
  );
};

// Mobile: плавающее превью над картой. Tap — во весь экран, свайп — другая камера,
// долгое нажатие — меню (камера 1 / 2 / обе).
export const CameraPreview: React.FC = () => {
  const cam = useUi(u => u.cam);
  const { go, setCam } = useUiActions();
  const start = useRef<{ x: number; y: number; t: number } | null>(null);
  const longTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [menu, setMenu] = useState(false);
  const one: CamId = cam === '1' ? '1' : '0';

  const down = (e: React.PointerEvent) => {
    start.current = { x: e.clientX, y: e.clientY, t: Date.now() };
    longTimer.current = setTimeout(() => {
      start.current = null;
      setMenu(true);
    }, 600);
  };
  const up = (e: React.PointerEvent) => {
    if (longTimer.current) clearTimeout(longTimer.current);
    const s = start.current;
    start.current = null;
    if (!s) return;
    const dx = e.clientX - s.x;
    if (Math.abs(dx) > 30) setCam(one === '0' ? '1' : '0');
    else if (Math.abs(e.clientY - s.y) < 15) go('camera');
  };

  return (
    <div className="op-preview">
      <div className="op-preview-body" onPointerDown={down} onPointerUp={up}
           onPointerCancel={() => longTimer.current && clearTimeout(longTimer.current)}
           onContextMenu={e => e.preventDefault()}>
        <CameraView cam={one} overlays={false} />
      </div>
      <Shutter cam={one} className="preview" />
      <button type="button" className="op-preview-swap" aria-label={`Переключить на ${one === '0' ? 'камеру 2' : 'камеру 1'}`}
              onClick={() => setCam(one === '0' ? '1' : '0')}>
        <Icon name="swap" size={16} />
      </button>
      {menu && (
        <div className="op-popover up right" role="menu" aria-label="Камера">
          {(['0', '1', 'split'] as CamChoice[]).map(c => (
            <button key={c} type="button" className={`op-menu-item ${cam === c ? 'on' : ''}`}
                    onClick={() => { setCam(c); setMenu(false); if (c === 'split') go('camera'); }}>
              {c === 'split' ? 'Обе камеры (во весь экран)' : CAM_LABELS[c]}
            </button>
          ))}
          <button type="button" className="op-menu-item" onClick={() => { setMenu(false); go('camera'); }}>Открыть во весь экран</button>
          <button type="button" className="op-menu-item" onClick={() => setMenu(false)}>Закрыть</button>
        </div>
      )}
    </div>
  );
};
